import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import _ from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ErrorIcon from '@mui/icons-material/ErrorRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import LockIcon from '@mui/icons-material/LockRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { getLogger } from 'app-components/utils/core';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { billPresentmentActions } from 'app-components/flux/bill-presentment';
import { useBillersWithLogos } from 'app-components/flux/bill-presentment/billPresentmentHooks';
import type {
  Biller,
  BillerAccount,
  EnrollResponse,
  FormField,
  FormParam,
} from 'app-components/flux/bill-presentment/billPresentmentTypes';
import {
  getFieldError,
  hasFormError,
  initFormError,
  initializeForm,
  makeParametersFromFormValues,
  trackFormEnter,
} from 'app-components/flux/bill-presentment/billPresentmentUtils';

import ConnectFIs from 'assets/illustrations/ConnectFIs.svg';
import Dump from 'components/Dump';

import { StepEnum } from '../types';
import LinkPreview from 'components/LinkPreview';

const log = getLogger('BillPresentment/LoginPage');

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: 432,
    flexGrow: 1,
    boxSizing: 'border-box',
    width: '100%',
    '& .MuiPaper-root': {
      overflowY: 'inherit', // ensure dialog does not scroll internally
    },
  },

  scrollContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: '12px',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },

  scrollInner: {
    width: '100%',
    maxWidth: 464,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    textAlign: 'left',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBottom: 24,
  },

  formActions: {
    height: 72,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    borderTop: `1px solid ${theme.palette.border.light}`,
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    flexShrink: 0,
  },

  footer: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },

  subLock: {
    height: 20,
    width: 20,
    marginRight: 8,
    color: theme.palette.icon.light,
  },

  loadingFade: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 64,
  },

  row: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingTop: 24,
    textAlign: 'center',
    alignContent: 'center',
    color: theme.palette.text.secondary,
  },
  brandingIcon: {
    position: 'absolute',
    left: 48,
    width: 64,
    height: 64,
    borderRadius: 12,
    objectFit: 'contain',
    border: `1px solid ${theme.palette.border.light}`,
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 64,
    flexShrink: 0,
    position: 'relative',
    padding: '0 144px',
    marginTop: 24,
  },
  billerDetails: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  billerClassification: {
    flexShrink: 0,
  },

  termButton: {
    padding: '8px 16px 8px 0',
    borderRadius: 8,
  },

  rotate: {
    transform: 'rotate(90deg)',
  },

  terms: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: 28,
  },

  infoIcon: {
    marginLeft: 2,
    marginRight: 2,
    height: 18,
  },

  tooltipText: {
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
  },

  tooltip: {
    backgroundColor: theme.palette.error.light,
  },

  tooltipArrow: {
    color: theme.palette.error.light,
  },

  tooltipStyle: {
    padding: 12,
    borderRadius: 8,
  },

  popper: {
    marginBottom: '8px !important',
  },
}));

interface LoginProps {
  enrollment?: EnrollResponse | null;
  biller?: Biller | null;
  setSteps: (data: any) => void;
  GUID: string;
  series: scheduledTransactionsTypes.ScheduledTransactionType;
}

const SubmittingOverlay = () => {
  const { classes } = useStyles();
  const [text, setText] = useState<string>('This won’t take long!');

  useEffect(() => {
    setTimeout(() => setText('Finishing up...'), 8000);
  }, []);

  return (
    <div className={classes.loadingFade}>
      <CircularProgress size={128} />
      <div className={classes.row}>
        <Typography variant='headlineRegular'>Verifying your account details.</Typography>
        <Typography variant='bodyRegular' sx={{ mt: 1 }}>
          {text}
        </Typography>
      </div>
    </div>
  );
};

const LoginView = ({ enrollment, biller, setSteps, GUID, series }: LoginProps) => {
  const { classes } = useStyles();
  const billerAccount: BillerAccount = useAppSelector((state) =>
    billPresentmentSelectors.selectBillerAccountById(state, series?.presentmentAccountId || ''),
  );
  const { billersWithLogos } = useBillersWithLogos(biller ? [biller] : []);
  const dispatch = useAppDispatch();
  const fieldRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const [formValues, setFormValues] = useState<FormParam[]>(() => initializeForm(enrollment));
  const [validate, setValidate] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(() => initFormError(billerAccount, series));

  const [showTerms, setShowTerms] = useState<boolean>();

  const handleFormSubmission = async () => {
    const hasError = hasFormError(formValues);
    setValidate(true);
    log.info('LoginView: handleFormSubmission', { formValues });

    trackFormEnter(formValues, hasError);

    if (!biller || hasError) return;

    setSubmitting(true);

    const parameters = makeParametersFromFormValues(formValues);

    if (hasError) {
      log.debug('Form has errors, scrolling to first error field');
      for (const param of formValues) {
        for (const field of param.fields) {
          const error = getFieldError(field);
          log.debug('Checking field for error:', field.name, 'Error:', error);
          if (error) {
            const ref = fieldRefs.current[field.name];
            if (ref && typeof ref.scrollIntoView === 'function') {
              log.debug('Scrolling to field with error:', field.name);
              // Scroll the field into view
              ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return;
            }
          }
        }
      }
    }

    const dataToSend = {
      nickName: biller.name,
      reference: GUID,
      billerId: biller.id,
      data: {
        consent: { value: 'Yes' },
        printSuppression: {
          value: enrollment?.printSuppression?.type === 'Mandatory' ? 'Yes' : 'No',
        },
        parameters,
      },
    };

    try {
      const updatedBillerAccount = await dispatch(
        billPresentmentActions.doEnrollment({ payload: dataToSend, series, biller }),
      ).unwrap();
      log.debug('updated Biller Account', updatedBillerAccount);

      if (!updatedBillerAccount || updatedBillerAccount.status === 'FAILED') {
        setSubmitting(false);
        setError(
          updatedBillerAccount?.additionalInfo?.description ||
            'There was an error connecting to this biller. Please try again later.',
        );
      } else if (updatedBillerAccount.status === 'PENDING') {
        setSteps(StepEnum.PENDING);
      } else if (updatedBillerAccount.status === 'CONNECTED') {
        // TODO: completed
        setSteps(StepEnum.PENDING);
      }
    } catch (err: any) {
      log.error('Submission failed', err);
      if (err?.response?.data?.title && err.response.data.detail) {
        setError(`${err.response.data.title} - ${err.response.data.detail}`);
      } else if (err?.message) {
        setError(`Something went wrong - ${err.message} - please check your fields and try again`);
      } else {
        setError(`Something went wrong - please check your fields and try again`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const websiteLink = biller?.url;

  const billerLogo = billersWithLogos[0]?.logoKey;

  return (
    <>
      <div className={classes.root} style={submitting ? { marginBottom: 72 } : undefined}>
        <Dump obj={biller} />

        <div className={classes.header}>
          <img alt='Bank Icon' className={classes.brandingIcon} src={billerLogo || ConnectFIs} />
          <Typography variant='titleMedium'>{biller?.name}</Typography>

          <div className={classes.billerDetails}>
            {websiteLink && <LinkPreview href={websiteLink} showFavicon={false} />}
            <Typography variant='paragraphRegular' noWrap>
              ·
            </Typography>

            <Typography className={classes.billerClassification} variant='paragraphRegular' color='textSecondary'>
              {biller?.classification && `${biller.classification}`}
            </Typography>
          </div>
        </div>

        {submitting ? (
          <SubmittingOverlay />
        ) : (
          <div className={classes.scrollContainer}>
            <div className={classes.scrollInner}>
              {error ? (
                <Alert severity='error'>{error}</Alert>
              ) : (
                <Typography variant='bodyRegular' margin='12px 0'>
                  Enter the following account details to connect.
                </Typography>
              )}

              <div className={classes.form}>
                <Dump obj={enrollment} />
                {formValues.map((parameter, pIndex) => {
                  const isUSAddress = parameter.name === 'us-address';

                  if (!parameter?.fields) return null;
                  return (
                    <Fragment key={parameter.name}>
                      {parameter.fields.length > 1 ? (
                        <Typography variant='captionMedium' marginTop={2}>
                          {parameter.label}
                        </Typography>
                      ) : null}

                      {isUSAddress ? (
                        <Box display='flex' width='100%' flexDirection='column' gap={1.5} mt={1}>
                          {/* Number + Street in a row */}
                          <Box display='flex' gap={2} width='100%'>
                            {parameter.fields
                              .reduce((acc, field) => {
                                if (['number', 'street'].includes(field.name)) {
                                  acc.push(field);
                                }
                                return acc;
                              }, [] as FormField[])
                              .map((field) => {
                                const fieldError = validate && getFieldError(field);
                                const flex = field.name === 'number' ? 1 : 2;

                                const fieldIndex = parameter.fields.findIndex((f) => f.name === field.name); // ✅ Use real index

                                return (
                                  <Box
                                    key={field.name}
                                    ref={(el: HTMLDivElement | null) => {
                                      fieldRefs.current[field.name] = el;
                                    }}
                                    flex={flex}
                                  >
                                    <TextField
                                      value={field.value}
                                      label={field.label}
                                      onChange={(e) => {
                                        setFormValues((oldForm) =>
                                          _.set(
                                            [...oldForm],
                                            [pIndex, 'fields', fieldIndex, 'value'],
                                            e?.target?.value,
                                          ),
                                        );
                                      }}
                                      fullWidth
                                      margin='dense'
                                      helperText={fieldError}
                                      error={Boolean(fieldError)}
                                    />
                                  </Box>
                                );
                              })}
                          </Box>
                          {/* Render all other fields normally, stacked */}
                          {parameter.fields
                            .reduce((acc, field) => {
                              if (!['number', 'street'].includes(field.name)) {
                                acc.push(field);
                              }
                              return acc;
                            }, [] as FormField[])
                            .map((field) => {
                              const fieldError = validate && getFieldError(field);

                              const fieldIndex = parameter.fields.findIndex((f) => f.name === field.name); // ✅ Use real index

                              return (
                                <TextField
                                  key={field.name}
                                  value={field.value}
                                  label={field.label}
                                  onChange={(e) => {
                                    setFormValues((oldForm) =>
                                      _.set([...oldForm], [pIndex, 'fields', fieldIndex, 'value'], e.target.value),
                                    );
                                  }}
                                  fullWidth
                                  margin='dense'
                                  helperText={fieldError}
                                  error={Boolean(fieldError)}
                                />
                              );
                            })}
                        </Box>
                      ) : (
                        parameter.fields.map((field, fIndex) => {
                          const fieldError = validate && getFieldError(field);
                          return (
                            <TextField
                              key={field.name}
                              value={field.value}
                              label={field.label}
                              onChange={(e) => {
                                setFormValues((oldForm) =>
                                  _.set([...oldForm], [pIndex, 'fields', fIndex, 'value'], e?.target?.value),
                                );
                              }}
                              fullWidth
                              margin='dense'
                              helperText={fieldError}
                              error={Boolean(fieldError)}
                            />
                          );
                        })
                      )}
                    </Fragment>
                  );
                })}

                <Divider flexItem sx={{ marginY: 1 }} />
                <ButtonBase id='terms-button' onClick={() => setShowTerms(!showTerms)} className={classes.termButton}>
                  <Typography variant='paragraphRegular' color='textSecondary'>
                    <ArrowRightIcon className={showTerms ? classes.rotate : ''} sx={{ marginRight: '4px' }} />
                    {`By connecting, you agree to your biller’s terms.`}
                  </Typography>
                </ButtonBase>

                {showTerms ? (
                  <div className={classes.terms}>
                    <Typography variant='paragraphMedium' color='textSecondary' marginBottom='2px'>
                      From {biller?.name}:
                    </Typography>
                    <Typography variant='captionRegular' color='textSecondary'>
                      {enrollment?.consent?.content}
                    </Typography>
                    <></>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      {!submitting && (
        <div className={classes.formActions}>
          <Typography variant='paragraphRegular' className={classes.footer} component='div'>
            <LockIcon className={classes.subLock} />
            Secure connection, encrypted end-to-end
          </Typography>

          <Tooltip
            arrow
            classes={{
              tooltip: classNames(classes.tooltip, classes.tooltipStyle),
              arrow: classes.tooltipArrow,
              tooltipPlacementTop: classes.popper,
            }}
            title={
              <div className={classes.tooltipText}>
                <ErrorIcon className={classes.infoIcon} />
                <Typography variant='paragraphRegular'> Fields need attention</Typography>
              </div>
            }
            placement='top'
            open={validate && hasFormError(formValues)}
          >
            <span>
              <Button id='submitButton' variant='contained' startIcon={<LinkIcon />} onClick={handleFormSubmission}>
                Connect
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
    </>
  );
};
export default LoginView;
